import React, { useEffect, useMemo, useState } from 'react';
import './Calendar.css';

interface CalendarProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  disabledDates?: string[];
  minDate?: Date;
  maxDate?: Date;
  bookedDates?: string[];
}

const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onDateSelect,
  disabledDates = [],
  minDate = new Date(),
  maxDate,
  bookedDates = []
}) => {
  const sanitizedMinDate = useMemo(() => {
    const date = new Date(minDate);
    date.setHours(0, 0, 0, 0);
    return date;
  }, [minDate]);

  const sanitizedMaxDate = useMemo(() => {
    if (!maxDate) return undefined;
    const date = new Date(maxDate);
    date.setHours(0, 0, 0, 0);
    return date;
  }, [maxDate]);

  const initialMonth = selectedDate ? new Date(selectedDate) : sanitizedMinDate;
  const [currentMonth, setCurrentMonth] = useState(initialMonth);

  useEffect(() => {
    if (!selectedDate) return;
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    if (Number.isNaN(selected.getTime())) return;
    setCurrentMonth(selected);
  }, [selectedDate]);

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isDateDisabled = (dateStr: string) => {
    const date = new Date(dateStr);
    const beforeMin = date < sanitizedMinDate;
    const afterMax = sanitizedMaxDate ? date > sanitizedMaxDate : false;
    return beforeMin || afterMax || disabledDates.includes(dateStr);
  };

  const goToPreviousMonth = () => {
    const previousMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    if (previousMonth < sanitizedMinDate) return;
    setCurrentMonth(previousMonth);
  };

  const goToNextMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    if (sanitizedMaxDate && nextMonth > sanitizedMaxDate) return;
    setCurrentMonth(nextMonth);
  };

  const prevMonthStart = useMemo(() => new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1), [currentMonth]);
  const nextMonthStart = useMemo(() => new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1), [currentMonth]);

  const minMonthStart = useMemo(() => new Date(sanitizedMinDate.getFullYear(), sanitizedMinDate.getMonth(), 1), [sanitizedMinDate]);
  const maxMonthStart = useMemo(() => sanitizedMaxDate ? new Date(sanitizedMaxDate.getFullYear(), sanitizedMaxDate.getMonth(), 1) : undefined, [sanitizedMaxDate]);

  const prevDisabled = prevMonthStart < minMonthStart;
  const nextDisabled = maxMonthStart ? nextMonthStart > maxMonthStart : false;

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);

    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDate(year, month, day);
      const isSelected = dateStr === selectedDate;
      const isDisabled = isDateDisabled(dateStr);
      const hasBooking = bookedDates.includes(dateStr);

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${hasBooking ? 'has-booking' : ''}`}
          onClick={() => !isDisabled && onDateSelect(dateStr)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button
          onClick={goToPreviousMonth}
          className="calendar-nav"
          disabled={prevDisabled}
        >
          &#8249;
        </button>
        <h3 className="calendar-title">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={goToNextMonth}
          className="calendar-nav"
          disabled={nextDisabled}
        >
          &#8250;
        </button>
      </div>
      <div className="calendar-weekdays">
        {dayNames.map(day => (
          <div key={day} className="calendar-weekday">
            {day}
          </div>
        ))}
      </div>
      <div className="calendar-grid">
        {renderCalendar()}
      </div>
    </div>
  );
};

export default Calendar;